import { Component, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SubscriptionLike   } from 'rxjs';

import { Room } from '@shared/factories';
import { ChatEventsService, SessionStorageService, UserService } from '@shared/services';
import { BULK_TAB, ChatTab  } from './chat-tabs/chat-tabs.component';
import { ChatBoxComponent   } from './chat-box/chat-box.component';
import { ChatsListComponent } from './chats-list/chats-list.component';

const MAX_TABS = 3;

@Component({
  selector:      'chat',
  templateUrl: './chat.component.html',
  host: { class: 'fixed bottom-0 left-full am-flex-column z-2' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChatComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  subscriptions:  SubscriptionLike[] = [];
  chatFullAccess: boolean;
  betaCheck:      boolean;

  rooms:          Room[]   = [];
  tabIds:         number[] = [];
  selectedTab:    ChatTab;
  bulkMode:       boolean;

  showChatList:   boolean;

  @ViewChildren('room') roomsList: QueryList<ChatBoxComponent>;
  @ViewChild(ChatsListComponent) chatsList: ChatsListComponent;

  @HostListener('document:keydown.escape')
  escape(): void {
    if (this.showChatList && !this.eRef.nativeElement.querySelector('[aria-expanded="true"]')) this.minimize();
  }

  constructor(
    private eRef:                  ElementRef,
    private chatEventsService:     ChatEventsService,
    private userService:           UserService,
    private sessionStorageService: SessionStorageService,
  ) { }

  ngOnInit(): void {
    this.sessionStorageService.pills.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(pills => this.updateView());
    this.userService.currentUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user  => this.updateView());
    this.sessionStorageService.chatFullAccess.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(state => this.chatFullAccess = state);
  }

  private updateView(): void {
    this.betaCheck = this.checkBeta();
    this.reloadChats();
  }

  private checkBeta(): boolean {
    let feature = this.userService.currentUserValue?.features?.chat_feature;
    if      (!feature)          return false;
    else if ( feature === true) return true;
    else if (!feature.length)   return false;

    let pills       = this.sessionStorageService.pillsValue || [];
    let activePills = pills.filter(p => p.active);
    return (activePills.length ? activePills : pills).some(p => feature.some(f => +f === +p.location));
  }

  private reloadChats(): void {
    if (this.betaCheck) {
      if (this.subscriptions.length) return;
      this.chatEventsService.changeActiveRoomsNumber(MAX_TABS);
      this.subscriptions.push(this.chatEventsService.rooms.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(rooms => this.updateRooms(rooms)));
      this.subscriptions.push(this.chatEventsService.roomOpened.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(room => this.roomOpened(room)));
    } else {
      if (this.showChatList) this.minimize();

      this.rooms          = null;
      this.tabIds         = [];
      this.selectedTab    = null;
      this.bulkMode       = null;
      this.showChatList   = null;

      this.chatEventsService.changeActiveRoomsNumber(null);
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
      this.subscriptions = [];
    }
  }

  private updateRooms(rooms: Room[]): void {
    this.rooms  = rooms;
    let active  = this.activeRooms;

    this.tabIds = this.tabIds.filter(id => active.some(r => r.id === id));
    active.forEach(r => { if (!this.tabIds.includes(r.id)) this.tabIds.push(r.id); });
    if (typeof this.selectedTab === 'number' && !this.tabIds.includes(this.selectedTab)) this.selectedTab = this.tabIds[this.tabIds.length-1] || null;
  }

  private roomOpened(room: Room): void {
    this.showChatList = true;
    this.showTab(room.id);
  }

  private showTab(tab: ChatTab): void {
    this.selectedTab = tab;
    if (typeof tab === 'number') setTimeout(() => this.roomsList?.find(r => r.room.id === tab)?.show());
  }

  selectTab(tab: ChatTab): void {
    this.showTab(tab);
  }

  openRoom(room: Room): void {
    this.chatEventsService.openRoom(room);
  }

  openBulkList(): void {
    if (!this.bulkMode) this.chatsList?.resetSearch();
    this.bulkMode = true;
    this.selectTab(BULK_TAB);
  }

  closeBulk(): void {
    this.bulkMode = false;
    if (this.selectedTab === BULK_TAB) this.selectTab(null);
  }

  toggleChatList(state: boolean): void {
    this.showChatList = state;
    if (state && this.selectedTab) this.showTab(this.selectedTab);
  }

  minimize(): void {
    if (this.chatsList) this.chatsList.closeList();
    else                this.toggleChatList(null);
  }

  get activeRooms(): Room[] {
    return this.rooms?.filter(r => r?.active) || [];
  }

  get tabs(): Room[] {
    return this.tabIds.map(id => this.activeRooms.find(r => r.id === id)).filter(r => !!r);
  }

}
